import { useCallback, useMemo, useState } from "react";
import { Check, ChevronDown, Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/library/command";
import { useUniversitySearch } from "@/features/universityAndCourse/api/useUniversities";

interface UniversityMultiComboboxProps {
  values: string[];
  onChange: (values: string[]) => void;
  hasError?: boolean;
  triggerId?: string;
  placeholder?: string;
}

/**
 * Multi-select twin of `UniversityCombobox` for ADD mode: the same course is
 * created in every university picked here. Same server-backed search
 * (`?search=…`, debounced) — a client-side filter would ship 500+ rows on
 * every drawer open.
 *
 * Picked universities render as removable chips under the trigger (a
 * chip's × can't live inside the trigger button — nested buttons). Labels
 * are remembered at pick time, so a university that drops out of the
 * current search page keeps its name on the chip without a per-id fetch.
 */
export default function UniversityMultiCombobox({
  values,
  onChange,
  hasError,
  triggerId,
  placeholder = "Select universities",
}: UniversityMultiComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 250);
  const { universities, isFetching } = useUniversitySearch(debounced);

  // id → name, captured when picked (and refreshed from any result page).
  const [labels, setLabels] = useState<Record<string, string>>({});
  const labelOf = useCallback(
    (id: string) =>
      labels[id] ??
      universities.find((u: any) => String(u.id) === id)?.name ??
      `#${id}`,
    [labels, universities],
  );

  const selected = useMemo(() => new Set(values), [values]);

  const toggle = (id: string, name: string) => {
    if (selected.has(id)) {
      onChange(values.filter((v) => v !== id));
      return;
    }
    setLabels((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }));
    onChange([...values, id]);
  };

  const remove = (id: string) => onChange(values.filter((v) => v !== id));

  const summary =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? labelOf(values[0])
        : `${values.length} universities selected`;

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={triggerId}
            type="button"
            aria-expanded={open}
            aria-haspopup="listbox"
            className={cn(
              "bg-[#fcfcfc] border border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm tracking-[-0.56px] w-full inline-flex items-center justify-between gap-2 outline-none cursor-pointer",
              hasError
                ? "border-red-500 focus-visible:ring-red-500"
                : "focus-visible:ring-2 focus-visible:ring-[#cdd1d0]",
            )}
          >
            <span
              className={cn(
                "truncate text-left",
                values.length > 0 ? "text-[#051912]" : "text-[#505e59]",
              )}
            >
              {summary}
            </span>
            <ChevronDown className="size-4 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="p-0 rounded-[12px] w-[var(--radix-popover-trigger-width)]"
        >
          {/* shouldFilter={false} hands filtering control to the backend. */}
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search universities…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-[280px]">
              {isFetching && universities.length === 0 ? (
                <div className="flex items-center justify-center gap-2 py-6 text-xs text-[#505e59]">
                  <Loader2 className="size-3 animate-spin" />
                  Searching…
                </div>
              ) : (
                <CommandEmpty>No university found.</CommandEmpty>
              )}
              {universities.map((u: any) => {
                const idStr = String(u.id);
                const isSelected = selected.has(idStr);
                return (
                  <CommandItem
                    key={idStr}
                    value={idStr}
                    // Stay open: picking several is the whole point here.
                    onSelect={() => toggle(idStr, String(u.name ?? ""))}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{u.name}</span>
                    {isSelected && (
                      <Check className="size-4 shrink-0 opacity-80" />
                    )}
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 max-w-full rounded-full border border-[#cdd1d0] bg-white pl-2.5 pr-1 py-0.5 text-xs text-[#374741] tracking-[-0.24px]"
            >
              <span className="truncate">{labelOf(id)}</span>
              <button
                type="button"
                aria-label={`Remove ${labelOf(id)}`}
                onClick={() => remove(id)}
                className="rounded-full p-0.5 text-[#697571] hover:bg-[#f0f2f1] hover:text-[#1e302a] cursor-pointer"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
